import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { AuthImg, UploadButton } from "../components/AuthImg";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Droplet, Camera, Disc3, Sparkles, Gauge, ChevronRight, Plus, Trash2, Calendar, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtRoDate } from "../lib/dateFmt";

const TYPE_META = {
  liquids:       { label: "Control lichide",        icon: Droplet,   color: "#0ea5e9", bg: "#e0f2fe", short: "ulei & antigel" },
  photos_actual: { label: "Fotografii actuale",     icon: Camera,    color: "#7c3aed", bg: "#ede9fe", short: "toate pozițiile + interior" },
  tires:         { label: "Profil cauciucuri",      icon: Disc3,     color: "#0d9488", bg: "#ccfbf1", short: "măsurători + poză" },
  cleaning:      { label: "Curățenie săptămânală",  icon: Sparkles,  color: "#16a34a", bg: "#dcfce7", short: "bord, scaune, buzunare" },
  km_friday:     { label: "Poza km",   icon: Gauge,     color: "#ea580c", bg: "#ffedd5", short: "kilometraj + poză" },
};
const TYPE_ORDER = ["liquids", "km_friday"];

export default function IntretinereMasini() {
  const [cars, setCars] = useState([]);
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState("");
  const [openType, setOpenType] = useState(null);
  const [historyType, setHistoryType] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [carsRes, cfgRes, sumRes] = await Promise.all([
        api.get("/me/cars"),
        api.get("/me/car-maintenance/config"),
        api.get("/me/car-maintenance/summary"),
      ]);
      setCars(carsRes.data || []);
      setConfig(cfgRes.data || null);
      setSummary(sumRes.data || []);
      if ((carsRes.data || []).length > 0 && !selectedCarId) {
        setSelectedCarId(carsRes.data[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectedCar = useMemo(
    () => cars.find((c) => c.id === selectedCarId) || null,
    [cars, selectedCarId]
  );

  const summaryFor = (type) =>
    summary.find((s) => s.car_id === selectedCarId && s.type === type) || null;

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Se încarcă...</div>;
  }
  if (cars.length === 0) {
    return (
      <div className="card-flat p-12 text-center text-zinc-500">
        <Camera size={36} className="mx-auto text-zinc-300 mb-3" />
        <div className="font-semibold">Nicio mașină disponibilă</div>
        <div className="text-sm mt-1">Contactează administratorul pentru a fi alocată o mașină.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intretinere-masini-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
          🚗 Întreținere mașini
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Raportează periodic starea mașinii. Toate înregistrările rămân în istoricul mașinii și ajută la urmărirea ei.
        </p>
      </div>

      {/* Selector mașină */}
      <div className="card-flat p-4">
        <Label className="small-label">🚙 Mașina</Label>
        <Select value={selectedCarId} onValueChange={setSelectedCarId}>
          <SelectTrigger className="mt-2" data-testid="car-selector"><SelectValue placeholder="Alege mașina..." /></SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            {cars.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.numar_inmatriculare ? `${c.numar_inmatriculare} · ` : ""}
                {[c.marca, c.model].filter(Boolean).join(" ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cardurile de tipuri */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TYPE_ORDER.map((type) => {
          const meta = TYPE_META[type];
          const Icon = meta.icon;
          const s = summaryFor(type);
          return (
            <div
              key={type}
              className="card-flat p-5 hover:shadow-md transition-shadow"
              style={{ borderLeft: `4px solid ${meta.color}` }}
              data-testid={`maint-card-${type}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="w-11 h-11 rounded-md grid place-items-center" style={{ background: meta.bg, color: meta.color }}>
                  <Icon size={22} />
                </div>
                {s && <Badge variant="outline" className="text-[10px]">{s.count} {s.count === 1 ? "intrare" : "intrări"}</Badge>}
              </div>
              <div className="mt-3">
                <div className="font-display font-bold text-lg leading-tight">{meta.label}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{meta.short}</div>
              </div>
              <div className="mt-3 min-h-[44px] text-[11px] text-zinc-600">
                {s ? (
                  <div className="bg-zinc-50 rounded p-2">
                    <div className="flex items-center gap-1.5"><Calendar size={11}/> Ultima: <strong>{fmtRoDate(s.last_ts) || s.last_ts?.slice(0, 10)}</strong></div>
                    {s.last_user && <div className="flex items-center gap-1.5 mt-0.5"><UserIcon size={11}/> {s.last_user}</div>}
                    {type === "km_friday" && s.data?.km && <div className="mt-0.5 font-bold">📊 {s.data.km.toLocaleString("ro-RO")} km</div>}
                    {type === "tires" && (s.data?.fl_mm || s.data?.fr_mm) && (
                      <div className="mt-0.5">🛞 FS:{s.data.fl_mm}mm FD:{s.data.fr_mm}mm SS:{s.data.rl_mm}mm SD:{s.data.rr_mm}mm</div>
                    )}
                  </div>
                ) : (
                  <div className="italic text-zinc-400 text-center py-2">Nicio înregistrare</div>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => setOpenType(type)}
                  className="flex-1 gap-1"
                  style={{ background: meta.color }}
                  data-testid={`maint-add-${type}`}
                >
                  <Plus size={14}/> Adaugă
                </Button>
                {s && (
                  <Button variant="outline" size="sm" onClick={() => setHistoryType(type)} data-testid={`maint-history-${type}`} className="gap-1">
                    Istoric <ChevronRight size={14}/>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openType && (
        <MaintenanceDialog
          type={openType}
          car={selectedCar}
          config={config}
          onClose={() => setOpenType(null)}
          onSaved={() => { setOpenType(null); load(); }}
        />
      )}
      {historyType && (
        <HistoryDialog
          type={historyType}
          car={selectedCar}
          onClose={() => setHistoryType(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
}


function MaintenanceDialog({ type, car, config, onClose, onSaved }) {
  const meta = TYPE_META[type];
  const [imageIds, setImageIds] = useState([]);
  const [videoIds, setVideoIds] = useState([]);
  const [oilImages, setOilImages] = useState([]);
  const [antifreezeImages, setAntifreezeImages] = useState([]);
  const [tires, setTires] = useState({ fl_mm: "", fr_mm: "", rl_mm: "", rr_mm: "" });
  const [cleaningSlots, setCleaningSlots] = useState({}); // { "Bord": [fid, ...], ... }
  const [photosSlots, setPhotosSlots] = useState({});
  const [km, setKm] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const positions =
    type === "cleaning" ? (config?.cleaning_positions || []) :
    type === "photos_actual" ? (config?.photos_actual_positions || []) : [];
  const slots = type === "cleaning" ? cleaningSlots : photosSlots;
  const setSlots = type === "cleaning" ? setCleaningSlots : setPhotosSlots;
  const setSlotImage = (pos, fid) => setSlots((prev) => ({ ...prev, [pos]: [...(prev[pos] || []), fid] }));
  const removeSlotImage = (pos, fid) => setSlots((prev) => ({ ...prev, [pos]: (prev[pos] || []).filter((x) => x !== fid) }));

  const save = async () => {
    if (!car) { toast.error("Selectează mai întâi mașina"); return; }
    setSubmitting(true);
    try {
      const body = { car_id: car.id, type, notes };
      if (type === "liquids") {
        body.data = { oil_image_ids: oilImages, antifreeze_image_ids: antifreezeImages };
        body.image_ids = [...oilImages, ...antifreezeImages];
      } else if (type === "tires") {
        body.data = {
          fl_mm: tires.fl_mm ? parseFloat(tires.fl_mm) : null,
          fr_mm: tires.fr_mm ? parseFloat(tires.fr_mm) : null,
          rl_mm: tires.rl_mm ? parseFloat(tires.rl_mm) : null,
          rr_mm: tires.rr_mm ? parseFloat(tires.rr_mm) : null,
        };
        body.image_ids = imageIds;
      } else if (type === "km_friday") {
        body.data = { km: parseFloat(km) };
        body.image_ids = imageIds;
      } else {
        body.data = { slots };
        body.image_ids = Object.values(slots).flat();
      }
      body.video_ids = videoIds;
      await api.post("/me/car-maintenance", body);
      toast.success("Înregistrare salvată");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maint-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-md grid place-items-center" style={{ background: meta.bg, color: meta.color }}>
              <meta.icon size={20} />
            </span>
            {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 -mt-2">
          {car ? `${car.numar_inmatriculare || ""} · ${[car.marca, car.model].filter(Boolean).join(" ")}` : "Mașină necunoscută"}
        </div>

        <div className="space-y-4 mt-3">
          {type === "liquids" && (
            <>
              <SlotUpload
                title="🛢️ Poză nivel ULEI"
                color="#fbbf24"
                imageIds={oilImages}
                onAdd={(fid) => setOilImages((p) => [...p, fid])}
                onRemove={(fid) => setOilImages((p) => p.filter((x) => x !== fid))}
                testIdPrefix="oil"
              />
              <SlotUpload
                title="❄️ Poză nivel ANTIGEL"
                color="#06b6d4"
                imageIds={antifreezeImages}
                onAdd={(fid) => setAntifreezeImages((p) => [...p, fid])}
                onRemove={(fid) => setAntifreezeImages((p) => p.filter((x) => x !== fid))}
                testIdPrefix="antifreeze"
              />
            </>
          )}

          {type === "tires" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "fl_mm", label: "🛞 Față stânga (mm)" },
                  { key: "fr_mm", label: "🛞 Față dreapta (mm)" },
                  { key: "rl_mm", label: "🛞 Spate stânga (mm)" },
                  { key: "rr_mm", label: "🛞 Spate dreapta (mm)" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label className="text-xs">{label}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={tires[key]}
                      onChange={(e) => setTires({ ...tires, [key]: e.target.value })}
                      placeholder="ex: 4.5"
                      data-testid={`tire-${key}`}
                    />
                  </div>
                ))}
              </div>
              <SlotUpload
                title="📷 Poze cu profilul cauciucurilor"
                color={meta.color}
                imageIds={imageIds}
                onAdd={(fid) => setImageIds((p) => [...p, fid])}
                onRemove={(fid) => setImageIds((p) => p.filter((x) => x !== fid))}
                testIdPrefix="tire-photo"
              />
            </>
          )}

          {type === "km_friday" && (
            <>
              <div>
                <Label className="text-xs">📊 Kilometraj actual *</Label>
                <Input
                  type="number"
                  step="1"
                  value={km}
                  onChange={(e) => setKm(e.target.value)}
                  placeholder="ex: 125000"
                  data-testid="km-value"
                  className="font-bold text-lg"
                />
              </div>
              <SlotUpload
                title="📷 Poză cu kilometrajul *"
                color={meta.color}
                imageIds={imageIds}
                onAdd={(fid) => setImageIds((p) => [...p, fid])}
                onRemove={(fid) => setImageIds((p) => p.filter((x) => x !== fid))}
                testIdPrefix="km-photo"
              />
            </>
          )}

          {(type === "cleaning" || type === "photos_actual") && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-600 italic">
                Încarcă cel puțin o poză la fiecare poziție listată mai jos.
              </div>
              {positions.map((pos) => (
                <SlotUpload
                  key={pos}
                  title={`📷 ${pos}`}
                  color={meta.color}
                  imageIds={slots[pos] || []}
                  onAdd={(fid) => setSlotImage(pos, fid)}
                  onRemove={(fid) => removeSlotImage(pos, fid)}
                  testIdPrefix={`slot-${pos.replace(/\s+/g, "-").toLowerCase()}`}
                />
              ))}
            </div>
          )}

          <div>
            <Label className="text-xs">📝 Observații (opțional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="maint-notes"
              placeholder="Ex: ulei aproape de minim, cauciucurile uzate ușor pe lateral..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="maint-cancel">Anulează</Button>
          <Button variant="outline" onClick={save} disabled={submitting} className="!text-red-600 !border-red-400 font-bold hover:!bg-red-50" data-testid="maint-save">
            {submitting ? "Salvare..." : "Salvează înregistrarea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SlotUpload({ title, color, imageIds, onAdd, onRemove, testIdPrefix }) {
  return (
    <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/50">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm font-bold" style={{ color }}>{title}</div>
        <div className="text-[11px] text-zinc-500">{imageIds.length} {imageIds.length === 1 ? "poză" : "poze"}</div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <UploadButton
          testId={`${testIdPrefix}-upload`}
          label="+ Adaugă poză"
          accept="image/*"
          onUploaded={(f) => onAdd(f.id)}
        />
      </div>
      {imageIds.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mt-2">
          {imageIds.map((fid, idx) => (
            <div key={fid} className="relative group rounded overflow-hidden border border-zinc-300 aspect-square bg-white">
              <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`${title} ${idx + 1}`} />
              <button
                type="button"
                onClick={() => onRemove(fid)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 grid place-items-center text-[10px]"
                data-testid={`${testIdPrefix}-remove-${idx}`}
              >✗</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function HistoryDialog({ type, car, onClose, onDeleted }) {
  const meta = TYPE_META[type];
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!car) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/car-maintenance", { params: { car_id: car.id, type } });
      setEntries(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, car?.id]);

  const onDel = async (id) => {
    if (!window.confirm("Sigur ștergi această intrare?")) return;
    try {
      await api.delete(`/me/car-maintenance/${id}`);
      toast.success("Șters");
      onDeleted?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-md grid place-items-center" style={{ background: meta.bg, color: meta.color }}>
              <meta.icon size={18}/>
            </span>
            Istoric — {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 -mt-2">
          {car ? `${car.numar_inmatriculare || ""} · ${[car.marca, car.model].filter(Boolean).join(" ")}` : ""}
        </div>
        {loading ? (
          <div className="text-center py-8 text-zinc-400">Se încarcă...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-zinc-400 italic">Nicio înregistrare pentru această combinație.</div>
        ) : (
          <ul className="space-y-3">
            {entries.map((e) => (
              <li key={e.id} className="card-flat p-3" data-testid={`history-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <strong>📅 {fmtRoDate(e.ts)}</strong>
                    <span className="text-zinc-500"> · de {e.user_label}</span>
                  </div>
                  <button
                    onClick={() => onDel(e.id)}
                    className="text-red-600 hover:text-red-800 text-[11px]"
                    title="Șterge"
                    data-testid={`history-del-${e.id}`}
                  ><Trash2 size={13} className="inline-block" /></button>
                </div>
                {type === "km_friday" && e.data?.km && (
                  <div className="text-base font-bold mt-1 text-orange-700">📊 {e.data.km.toLocaleString("ro-RO")} km</div>
                )}
                {type === "tires" && (
                  <div className="text-xs mt-1 grid grid-cols-2 gap-1">
                    <div>🛞 Față stânga: <strong>{e.data?.fl_mm ?? "—"} mm</strong></div>
                    <div>🛞 Față dreapta: <strong>{e.data?.fr_mm ?? "—"} mm</strong></div>
                    <div>🛞 Spate stânga: <strong>{e.data?.rl_mm ?? "—"} mm</strong></div>
                    <div>🛞 Spate dreapta: <strong>{e.data?.rr_mm ?? "—"} mm</strong></div>
                  </div>
                )}
                {e.notes && <div className="text-xs italic text-zinc-600 mt-1">&bdquo;{e.notes}&rdquo;</div>}
                {(e.image_ids || []).length > 0 && (
                  <div className="grid grid-cols-6 gap-1 mt-2">
                    {e.image_ids.map((fid, idx) => (
                      <a
                        key={fid}
                        href={fileUrl(fid)}
                        target="_blank"
                        rel="noreferrer"
                        className="aspect-square block rounded overflow-hidden border border-zinc-200 hover:border-zinc-500"
                      >
                        <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Foto ${idx + 1}`}/>
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
